import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useReportContext } from "./reports/ReportContext";
import {
  getUserProfile,
  getAllTests,
  getAllTestsByOrg,
  getTestsForUser,
  getAllOrganizations,
  getOpenCorrectionRequests,
  getOrganizationDetails,
} from "./firestoreService";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Chip,
  Checkbox,
  IconButton,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";

function FindTest() {
  const { userRole, currentUser } = useAuth();
  const reportContext = useReportContext();
  const isReportMode = !!reportContext?.isReportContext;
  const [allTests, setAllTests] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [openRequests, setOpenRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTests, setSelectedTests] = useState(new Set());

  const { t } = useTranslation();

  const [filters, setFilters] = useState({
    caseId: "",
    patientId: "",
    date: "",
    organization: "all",
  });

  // Sorting state - default to most recent first
  const [sortColumn, setSortColumn] = useState("timestamp");
  const [sortDirection, setSortDirection] = useState("desc");

  const requestedTestIds = location.state?.requestedTestIds;

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (!currentUser) return;

      const profile = await getUserProfile(currentUser.uid);
      const orgId = profile?.organizationId || null;

      let testsPromise;
      let orgsPromise = Promise.resolve([]);
      let requestsPromise = getOpenCorrectionRequests(
        userRole === "superuser" ? null : orgId,
      );

      if (userRole === "superuser") {
        testsPromise = getAllTests();
        orgsPromise = getAllOrganizations();
      } else if (userRole === "admin" || userRole === "pro_user") {
        testsPromise = getAllTestsByOrg(orgId);
        orgsPromise = getOrganizationDetails(orgId).then((org) =>
          org ? [{ id: orgId, ...org }] : [],
        );
        setFilters((prev) => ({ ...prev, organization: orgId }));
      } else {
        // 'user' role
        testsPromise = getTestsForUser(currentUser.uid);
      }

      const [tests, orgs, requests] = await Promise.all([
        testsPromise,
        orgsPromise,
        requestsPromise,
      ]);

      setAllTests(tests);
      setOrganizations(
        userRole === "superuser"
          ? orgs.filter((o) => o.id !== "dd_quick_platform")
          : orgs,
      );
      setOpenRequests(requests);
    } catch (err) {
      console.error(err);
      setError(t("findTest.loadError"));
    } finally {
      setLoading(false);
    }
  }, [userRole, currentUser, t]);

  useEffect(() => {
    if (userRole && currentUser) {
      fetchAllData();
    }
  }, [userRole, currentUser, fetchAllData]);

  const openRequestTestIds = useMemo(
    () => new Set(openRequests.map((req) => req.testId)),
    [openRequests],
  );

  const filteredTests = useMemo(() => {
    let testsToFilter = allTests;
    if (requestedTestIds) {
      const requestedIdsSet = new Set(requestedTestIds);
      testsToFilter = allTests.filter((test) => requestedIdsSet.has(test.id));
    }

    const filtered = testsToFilter.filter((test) => {
      const caseIdMatch = filters.caseId
        ? test.caseId?.toLowerCase().includes(filters.caseId.toLowerCase())
        : true;
      const patientIdMatch = filters.patientId
        ? test.clientPatientName
            ?.toLowerCase()
            .includes(filters.patientId.toLowerCase())
        : true;
      const dateMatch = filters.date
        ? new Date(test.timestamp.seconds * 1000).toLocaleDateString() ===
          new Date(filters.date + "T00:00:00").toLocaleDateString()
        : true;
      const orgMatch =
        userRole === "superuser" && filters.organization !== "all"
          ? test.organizationId === filters.organization
          : true;
      return caseIdMatch && patientIdMatch && dateMatch && orgMatch;
    });

    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case "caseId":
          aValue = a.caseId?.toLowerCase() || "";
          bValue = b.caseId?.toLowerCase() || "";
          break;
        case "clientPatientName":
          aValue = a.clientPatientName?.toLowerCase() || "";
          bValue = b.clientPatientName?.toLowerCase() || "";
          break;
        case "timestamp":
          aValue = a.timestamp?.seconds || 0;
          bValue = b.timestamp?.seconds || 0;
          break;
        case "organizationId":
          // Sort by organization shortName for better UX
          const orgA = organizations.find((o) => o.id === a.organizationId);
          const orgB = organizations.find((o) => o.id === b.organizationId);
          aValue =
            orgA?.shortName?.toLowerCase() || orgA?.name?.toLowerCase() || "";
          bValue =
            orgB?.shortName?.toLowerCase() || orgB?.name?.toLowerCase() || "";
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [
    allTests,
    filters,
    userRole,
    requestedTestIds,
    sortColumn,
    sortDirection,
    organizations,
  ]);

  // Handle sort column click
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New column - default to ascending, except timestamp which defaults to descending
      setSortColumn(column);
      setSortDirection(column === "timestamp" ? "desc" : "asc");
    }
  };

  const handleFilterChange = (e) =>
    setFilters({ ...filters, [e.target.name]: e.target.value });
  const clearFilters = () =>
    setFilters({
      caseId: "",
      patientId: "",
      date: "",
      organization: userRole === "admin" ? filters.organization : "all",
    });

  const handleSelectTest = (testId) => {
    setSelectedTests((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(testId)) {
        newSelected.delete(testId);
      } else {
        newSelected.add(testId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allVisibleIds = new Set(filteredTests.map((t) => t.id));
      setSelectedTests(allVisibleIds);
    } else {
      setSelectedTests(new Set());
    }
  };

  // Function to generate and download the CSV
  const handleDownloadCsv = () => {
    const testsToExport = allTests.filter((t) => selectedTests.has(t.id));
    if (testsToExport.length === 0) return;

    const headers = [
      // Test-level fields
      "assessmentId",
      "caseId",
      "submitterUid",
      "organizationId",
      "timestamp",
      "organism",
      "organismMo",
      "species",
      "clinicalIndication",
      "clinicalIndicationCode",
      "specimenType",
      "notes",
      // Per-disk fields
      "antibioticSymbol",
      "antibioticName",
      "ab",
      "diameterMm",
      "mode",
      "interpretation",
      "interpretationText",
      "guideline",
      "breakpointS",
      "breakpointR",
      "hasBreakpoint",
    ];

    let csvContent = headers.join(",") + "\n";

    testsToExport.forEach((test) => {
      const testTimestamp = new Date(
        test.timestamp.seconds * 1000,
      ).toISOString();
      test.diskAssessments.forEach((assessment) => {
        const row = [
          // Test-level fields
          test.id,
          `"${test.caseId || ""}"`,
          test.submitterUid,
          test.organizationId,
          testTimestamp,
          `"${test.organism || ""}"`,
          `"${test.organismMo || ""}"`,
          `"${test.species || ""}"`,
          `"${test.clinicalIndication || ""}"`,
          `"${test.clinicalIndicationCode || ""}"`,
          `"${test.specimenType || ""}"`,
          `"${(test.notes || "").replace(/"/g, '""')}"`,
          // Per-disk fields
          assessment.antibioticSymbol || "",
          `"${assessment.antibioticName || ""}"`,
          assessment.ab || "",
          assessment.diameterMm ?? "",
          assessment.mode || "",
          assessment.interpretation || "",
          `"${assessment.interpretationText || ""}"`,
          `"${assessment.guideline || ""}"`,
          assessment.breakpointS ?? "",
          assessment.breakpointR ?? "",
          assessment.hasBreakpoint ?? "",
        ];
        csvContent += row.join(",") + "\n";
      });
    });

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "kbquick_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getPageTitle = () => {
    if (isReportMode) {
      return t("findTest.reportTitle");
    }
    if (userRole === "user") {
      return t("nav.myTestHistory");
    }
    return t("findTest.title");
  };

  if (loading)
    return <CircularProgress sx={{ display: "block", margin: "2rem auto" }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1">
            {getPageTitle()}
          </Typography>

          {isReportMode && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("findTest.reportModeHelp")}
            </Typography>
          )}
        </Box>
        {selectedTests.size > 0 && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCsv}
            sx={{
              ml: 2,
            }}
          >
            {t("findTest.downloadSelected", { count: selectedTests.size })}
          </Button>
        )}
      </Box>
      <Paper elevation={2}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              {/* Filter Row */}
              <TableRow
                sx={{ "& .MuiTableCell-root": { backgroundColor: "#e0e0e0" } }}
              >
                <TableCell></TableCell> {/* Spacer for checkbox column */}
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    variant="standard"
                    name="caseId"
                    value={filters.caseId}
                    onChange={handleFilterChange}
                    placeholder={t("findTest.filterByCaseId")}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    variant="standard"
                    type="date"
                    name="date"
                    value={filters.date}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </TableCell>
                {(userRole === "admin" || userRole === "superuser") && (
                  <TableCell>
                    <FormControl fullWidth variant="standard">
                      <InputLabel>{t("common.organization")}</InputLabel>
                      <Select
                        name="organization"
                        value={filters.organization}
                        onChange={handleFilterChange}
                        disabled={userRole === "admin"}
                      >
                        {userRole === "superuser" && (
                          <MenuItem value="all">
                            {t("findTest.allOrganizations")}
                          </MenuItem>
                        )}
                        {organizations.map((org) => (
                          <MenuItem key={org.id} value={org.id}>
                            {org.shortName || org.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                )}
                {userRole === "user" && <TableCell />}
                <TableCell>
                  <Button size="small" onClick={clearFilters}>
                    {t("findTest.clear")}
                  </Button>
                </TableCell>
              </TableRow>
              {/* Header Row */}
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold",
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedTests.size > 0 &&
                      selectedTests.size < filteredTests.length
                    }
                    checked={
                      filteredTests.length > 0 &&
                      selectedTests.size === filteredTests.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell
                  sortDirection={
                    sortColumn === "caseId" ? sortDirection : false
                  }
                >
                  <TableSortLabel
                    active={sortColumn === "caseId"}
                    direction={sortColumn === "caseId" ? sortDirection : "asc"}
                    onClick={() => handleSort("caseId")}
                  >
                    {t("findTest.caseId")}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    sortColumn === "timestamp" ? sortDirection : false
                  }
                >
                  <TableSortLabel
                    active={sortColumn === "timestamp"}
                    direction={
                      sortColumn === "timestamp" ? sortDirection : "desc"
                    }
                    onClick={() => handleSort("timestamp")}
                  >
                    {t("findTest.dateSubmitted")}
                  </TableSortLabel>
                </TableCell>
                {userRole === "superuser" && (
                  <TableCell
                    sortDirection={
                      sortColumn === "organizationId" ? sortDirection : false
                    }
                  >
                    <TableSortLabel
                      active={sortColumn === "organizationId"}
                      direction={
                        sortColumn === "organizationId" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("organizationId")}
                    >
                      {t("common.organization")}
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell align="right">{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTests.map((test) => (
                <TableRow
                  key={test.id}
                  hover
                  selected={selectedTests.has(test.id)}
                >
                  {/* Row Checkbox */}
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedTests.has(test.id)}
                      onChange={() => handleSelectTest(test.id)}
                    />
                  </TableCell>
                  <TableCell>
                    {test.caseId}
                    {openRequestTestIds.has(test.id) && (
                      <Chip
                        label={t("findTest.editRequested")}
                        color="warning"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(test.timestamp.seconds * 1000).toLocaleString()}
                  </TableCell>
                  {userRole === "superuser" && (
                    <TableCell>
                      {organizations.find((o) => o.id === test.organizationId)
                        ?.shortName || "N/A"}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      {isReportMode ? (
                        /* Reports flow: Print icon only - goes directly to print page */
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() =>
                            navigate(`/app/print-report/${test.id}`)
                          }
                          title={t("viewTest.printDownload")}
                        >
                          <PrintIcon />
                        </IconButton>
                      ) : (
                        /* Find/Edit flow: View Details + Edit buttons */
                        <>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() =>
                              navigate(`/app/view-test/${test.id}`)
                            }
                          >
                            {t("findTest.viewDetails")}
                          </Button>
                          {(userRole === "admin" ||
                            userRole === "superuser" ||
                            userRole === "pro_user") && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() =>
                                navigate(`/app/edit-test/${test.id}`)
                              }
                            >
                              {t("common.edit")}
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
}

export default FindTest;
